import React, { useState, useEffect, useCallback } from 'react';
import {
    GitBranch, Plus, Search, Loader2, AlertTriangle, MoreHorizontal,
    Play, Pause, Edit2, Copy, Trash2, BarChart3, Calendar, CheckCircle,
    Clock, Zap, Shield, Package, CalendarDays, X
} from 'lucide-react';
import { fetchDealFlows, createDealFlow, updateDealFlow, deleteDealFlow, duplicateDealFlow, toggleDealFlowStatus } from '../../api/dealflow';
import FlowBuilder from './FlowBuilder';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import { FLOW_TEMPLATES } from './FlowTemplates';

// Template Icons Map
const TEMPLATE_ICONS = {
    Plus: Plus,
    Zap: Zap,
    Shield: Shield,
    Package: Package,
    Calendar: CalendarDays,
};

// Template Selector Modal
const TemplateSelector = ({ onSelect, onClose }) => {
    const [selectedId, setSelectedId] = useState(null);

    const getColorClasses = (color) => {
        const colors = {
            slate: 'bg-slate-500/10 border-slate-500/30 text-slate-400',
            emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500',
            amber: 'bg-amber-500/10 border-amber-500/30 text-amber-500',
            violet: 'bg-violet-500/10 border-violet-500/30 text-violet-500',
            sky: 'bg-sky-500/10 border-sky-500/30 text-sky-500',
        };
        return colors[color] || colors.slate;
    };

    const handleSelect = () => {
        const template = FLOW_TEMPLATES.find(t => t.id === selectedId);
        if (template) {
            onSelect(template);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
            <div className="bg-[#0d1424] border border-white/10 rounded-2xl shadow-2xl max-w-3xl w-full mx-4 overflow-hidden animate-fadeIn">
                {/* Header */}
                <div className="p-6 border-b border-white/10">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-white">Choose a Template</h2>
                            <p className="text-sm text-white/50 mt-1">Start with a pre-built flow or create from scratch</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Templates Grid */}
                <div className="p-6 max-h-[60vh] overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {FLOW_TEMPLATES.map((template) => {
                            const IconComponent = TEMPLATE_ICONS[template.icon] || GitBranch;
                            const isSelected = selectedId === template.id;
                            const colorClasses = getColorClasses(template.color);

                            return (
                                <button
                                    key={template.id}
                                    onClick={() => setSelectedId(template.id)}
                                    className={`p-5 rounded-xl border-2 text-left transition-all ${
                                        isSelected
                                            ? 'border-[#ff4d4d] bg-[#ff4d4d]/10 ring-2 ring-[#ff4d4d]/30'
                                            : 'border-white/10 hover:border-white/20 bg-white/[0.02] hover:bg-white/[0.05]'
                                    }`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className={`p-3 rounded-xl border ${colorClasses}`}>
                                            <IconComponent size={24} />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-semibold text-white">{template.name}</h3>
                                            <p className="text-sm text-white/50 mt-1">{template.description}</p>
                                            {template.nodes.length > 1 && (
                                                <div className="flex items-center gap-3 mt-3 text-xs text-white/40">
                                                    <span>{template.nodes.length} steps</span>
                                                    <span>{template.connections.length} connections</span>
                                                </div>
                                            )}
                                        </div>
                                        {isSelected && (
                                            <div className="w-6 h-6 rounded-full bg-[#ff4d4d] flex items-center justify-center">
                                                <CheckCircle size={14} className="text-white" />
                                            </div>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-white/10 bg-white/[0.02] flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/15 text-white rounded-xl font-medium transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSelect}
                        disabled={!selectedId}
                        className="flex-1 px-4 py-3 bg-[#ff4d4d] hover:bg-[#ff5c5c] disabled:opacity-50 disabled:hover:bg-[#ff4d4d] text-white rounded-xl font-medium transition flex items-center justify-center gap-2"
                    >
                        <Plus size={18} />
                        Use This Template
                    </button>
                </div>
            </div>
        </div>
    );
};

const DealFlowPage = ({ userEmail }) => {
    // Data state
    const [flows, setFlows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // UI state
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [editingFlow, setEditingFlow] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [menuOpen, setMenuOpen] = useState(null);
    const [showTemplateSelector, setShowTemplateSelector] = useState(false);

    // Load flows
    const loadFlows = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchDealFlows({ status: statusFilter !== 'all' ? statusFilter : undefined });
            setFlows(res.data || []);
        } catch (err) {
            // Don't show error for 404 (API not implemented yet) - just show empty state
            // User can still create flows from templates
            console.warn('[DealFlow] API not available:', err.message);
            setFlows([]);
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        loadFlows();
    }, [loadFlows]);

    // Filter flows
    const filteredFlows = flows.filter(flow => {
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            if (!flow.name?.toLowerCase().includes(term) &&
                !flow.description?.toLowerCase().includes(term)) {
                return false;
            }
        }
        return true;
    });

    // Create from template
    const handleSelectTemplate = (template) => {
        // Generate unique IDs for the new flow
        const timestamp = Date.now();
        const newNodes = template.nodes.map((node, index) => ({
            ...node,
            id: `${node.type}_${timestamp}_${index}`,
        }));

        // Update connection references
        const idMap = {};
        template.nodes.forEach((node, index) => {
            idMap[node.id] = newNodes[index].id;
        });

        const newConnections = template.connections.map((conn, index) => ({
            ...conn,
            id: `conn_${timestamp}_${index}`,
            from: idMap[conn.from] || conn.from,
            to: idMap[conn.to] || conn.to,
        }));

        setEditingFlow({
            id: null,
            name: template.id === 'blank' ? 'New Deal Flow' : `${template.name}`,
            description: template.description,
            status: 'draft',
            nodes: newNodes,
            connections: newConnections,
        });
        setShowTemplateSelector(false);
    };

    const handleEditFlow = (flow) => {
        setEditingFlow(flow);
        setMenuOpen(null);
    };

    const handleSaveFlow = async (flowData) => {
        setIsSaving(true);
        try {
            let savedFlow;
            if (flowData.id) {
                const res = await updateDealFlow(flowData.id, flowData);
                savedFlow = res.data;
                setFlows(prev => prev.map(f => f.id === flowData.id ? savedFlow : f));
            } else {
                const res = await createDealFlow(flowData);
                savedFlow = res.data;
                setFlows(prev => [savedFlow, ...prev]);
            }
            setEditingFlow(savedFlow);
            return savedFlow;
        } catch (err) {
            console.error('Failed to save flow:', err);
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteFlow = async (flowId) => {
        try {
            await deleteDealFlow(flowId);
            setFlows(prev => prev.filter(f => f.id !== flowId));
            setDeleteConfirm(null);
        } catch (err) {
            console.error('Failed to delete flow:', err);
            alert('Failed to delete: ' + err.message);
        }
    };

    const handleDuplicateFlow = async (flowId) => {
        try {
            const res = await duplicateDealFlow(flowId);
            setFlows(prev => [res.data, ...prev]);
            setMenuOpen(null);
        } catch (err) {
            console.error('Failed to duplicate flow:', err);
            alert('Failed to duplicate: ' + err.message);
        }
    };

    const handleToggleStatus = async (flow) => {
        const newStatus = flow.status === 'active' ? 'draft' : 'active';
        try {
            const res = await toggleDealFlowStatus(flow.id, newStatus);
            setFlows(prev => prev.map(f => f.id === flow.id ? res.data : f));
            setMenuOpen(null);
        } catch (err) {
            console.error('Failed to toggle status:', err);
            alert('Failed to update status: ' + err.message);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    // Show builder if editing
    if (editingFlow) {
        return (
            <FlowBuilder
                flow={editingFlow}
                onSave={handleSaveFlow}
                onBack={() => {
                    setEditingFlow(null);
                    loadFlows();
                }}
                isSaving={isSaving}
            />
        );
    }

    // List view
    return (
        <div className="flex flex-col h-full bg-[#050810]">
            {/* Template Selector Modal */}
            {showTemplateSelector && (
                <TemplateSelector
                    onSelect={handleSelectTemplate}
                    onClose={() => setShowTemplateSelector(false)}
                />
            )}

            {/* Hero */}
            <div className="relative overflow-hidden px-6 py-10 sm:py-12">
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(120deg, rgba(255, 77, 77, 0.95), rgba(15, 23, 42, 0.95))'
                    }}
                />
                <div
                    className="absolute inset-0 opacity-70"
                    style={{
                        backgroundImage: 'radial-gradient(circle at 15% 15%, rgba(255,255,255,0.3), transparent 40%), radial-gradient(circle at 80% 10%, rgba(255,255,255,0.2), transparent 35%)'
                    }}
                />
                <div className="relative max-w-7xl mx-auto">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                        <div className="text-white">
                            <p className="text-sm uppercase tracking-[0.2em] text-white/70">Deal Flow Builder</p>
                            <h2 className="text-3xl sm:text-4xl font-semibold mt-2">Create conversation scripts</h2>
                            <p className="text-sm sm:text-base text-white/80 mt-2 max-w-xl">
                                Build branching conversation flows that guide AI to close deals automatically.
                            </p>
                        </div>
                        <button
                            onClick={() => setShowTemplateSelector(true)}
                            className="px-6 py-3 bg-white text-[#ff4d4d] hover:bg-white/90 rounded-xl font-semibold flex items-center gap-2 transition shadow-lg"
                        >
                            <Plus size={20} />
                            Create New Flow
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-auto px-6 pb-6 -mt-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {[
                        { label: 'Total Flows', value: flows.length, icon: GitBranch, color: 'text-[#ff4d4d]' },
                        { label: 'Active', value: flows.filter(f => f.status === 'active').length, icon: Play, color: 'text-emerald-500' },
                        { label: 'Drafts', value: flows.filter(f => f.status === 'draft').length, icon: Edit2, color: 'text-amber-500' },
                        { label: 'Conversions', value: '0', icon: CheckCircle, color: 'text-sky-500' },
                    ].map((stat, i) => (
                        <div
                            key={stat.label}
                            className="p-5 bg-[#0d1424]/80 backdrop-blur border border-white/10 rounded-2xl shadow-xl animate-fadeIn"
                            style={{ animationDelay: `${i * 50}ms` }}
                        >
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/5 rounded-lg">
                                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-white/40 uppercase tracking-wider">{stat.label}</p>
                                    <p className={`text-2xl font-bold ${stat.color}`}>
                                        {loading ? '-' : stat.value}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="p-4 bg-[#0d1424]/80 backdrop-blur border border-white/10 rounded-2xl shadow-xl mb-6">
                    <div className="flex flex-wrap items-center gap-4">
                        {/* Search */}
                        <div className="relative flex-1 min-w-[200px] max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                            <input
                                type="text"
                                placeholder="Search flows..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition"
                            />
                        </div>

                        {/* Status Filter */}
                        <div className="flex bg-white/5 p-1 rounded-lg">
                            {['all', 'active', 'draft'].map(status => (
                                <button
                                    key={status}
                                    onClick={() => setStatusFilter(status)}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                                        statusFilter === status
                                            ? 'bg-[#ff4d4d] text-white'
                                            : 'text-white/50 hover:text-white'
                                    }`}
                                >
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <Loader2 size={32} className="text-[#ff4d4d] animate-spin mb-3" />
                        <p className="text-sm text-white/50">Loading flows...</p>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <div className="p-4 rounded-full bg-red-500/10 mb-4">
                            <AlertTriangle size={32} className="text-red-500" />
                        </div>
                        <p className="text-sm text-red-500 mb-4">{error}</p>
                        <button
                            onClick={loadFlows}
                            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition"
                        >
                            Try Again
                        </button>
                    </div>
                ) : filteredFlows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <div className="p-6 rounded-2xl bg-white/5 mb-4">
                            <GitBranch size={48} className="text-white/30" />
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-2">
                            {searchTerm ? 'No matching flows' : 'No deal flows yet'}
                        </h3>
                        <p className="text-sm text-white/50 mb-6 text-center max-w-md">
                            {searchTerm
                                ? 'Try a different search term.'
                                : 'Create your first deal flow to automate customer conversations and close more deals.'}
                        </p>
                        {!searchTerm && (
                            <button
                                onClick={() => setShowTemplateSelector(true)}
                                className="px-4 py-2 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg font-medium flex items-center gap-2 transition"
                            >
                                <Plus size={18} />
                                Create First Flow
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {filteredFlows.map((flow, index) => (
                            <div
                                key={flow.id}
                                className="p-5 bg-[#0d1424]/80 backdrop-blur border border-white/10 rounded-2xl hover:border-white/20 transition-all animate-fadeIn group"
                                style={{ animationDelay: `${index * 30}ms` }}
                            >
                                <div className="flex items-start justify-between">
                                    <div
                                        className="flex-1 cursor-pointer"
                                        onClick={() => handleEditFlow(flow)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${
                                                flow.status === 'active' ? 'bg-emerald-500/10' : 'bg-white/5'
                                            }`}>
                                                <GitBranch className={`w-5 h-5 ${
                                                    flow.status === 'active' ? 'text-emerald-500' : 'text-white/50'
                                                }`} />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-white group-hover:text-[#ff4d4d] transition">
                                                    {flow.name}
                                                </h3>
                                                <p className="text-sm text-white/50 mt-0.5 line-clamp-1">
                                                    {flow.description || 'No description'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-6 mt-4 text-sm">
                                            <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${
                                                flow.status === 'active'
                                                    ? 'bg-emerald-500/10 text-emerald-500'
                                                    : 'bg-white/5 text-white/50'
                                            }`}>
                                                {flow.status === 'active' ? <Play size={12} /> : <Clock size={12} />}
                                                {flow.status === 'active' ? 'Active' : 'Draft'}
                                            </span>
                                            <span className="flex items-center gap-1.5 text-white/40">
                                                <Calendar size={14} />
                                                {formatDate(flow.updated_at || flow.created_at)}
                                            </span>
                                            <span className="flex items-center gap-1.5 text-white/40">
                                                <BarChart3 size={14} />
                                                {flow.stats?.conversations || 0} conversations
                                            </span>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="relative">
                                        <button
                                            onClick={() => setMenuOpen(menuOpen === flow.id ? null : flow.id)}
                                            className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition"
                                        >
                                            <MoreHorizontal size={18} />
                                        </button>

                                        {menuOpen === flow.id && (
                                            <>
                                                <div
                                                    className="fixed inset-0 z-10"
                                                    onClick={() => setMenuOpen(null)}
                                                />
                                                <div className="absolute right-0 top-full mt-1 w-48 bg-[#0d1424] border border-white/10 rounded-xl shadow-xl z-20 overflow-hidden">
                                                    <button
                                                        onClick={() => handleEditFlow(flow)}
                                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-white/70 hover:bg-white/5 transition"
                                                    >
                                                        <Edit2 size={14} />
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDuplicateFlow(flow.id)}
                                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-white/70 hover:bg-white/5 transition"
                                                    >
                                                        <Copy size={14} />
                                                        Duplicate
                                                    </button>
                                                    <button
                                                        onClick={() => handleToggleStatus(flow)}
                                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-white/70 hover:bg-white/5 transition"
                                                    >
                                                        {flow.status === 'active' ? (
                                                            <>
                                                                <Pause size={14} />
                                                                Deactivate
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Play size={14} />
                                                                Activate
                                                            </>
                                                        )}
                                                    </button>
                                                    <div className="border-t border-white/10" />
                                                    <button
                                                        onClick={() => {
                                                            setMenuOpen(null);
                                                            setDeleteConfirm(flow);
                                                        }}
                                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-500/10 transition"
                                                    >
                                                        <Trash2 size={14} />
                                                        Delete
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Delete Confirmation */}
            {deleteConfirm && (
                <ConfirmDeleteModal
                    isOpen={true}
                    onConfirm={() => handleDeleteFlow(deleteConfirm.id)}
                    onCancel={() => setDeleteConfirm(null)}
                    title="Delete Deal Flow"
                    message={`Are you sure you want to delete "${deleteConfirm.name}"? This action cannot be undone.`}
                />
            )}
        </div>
    );
};

export default DealFlowPage;
